import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button, Card } from '@/components/ui';
import { useBalticStore } from '@/store/balticStore';
import { BalticPreviewSkeleton } from './BalticPreviewSkeleton';
import { DailyReportSectionHeader } from './DailyReportSectionHeader';
import { BalticService } from '@/services/balticService';
import type { BalticHistoryPoint, BalticHistoryRequest } from '@/types/baltic.types';
import { formatBalticDate } from '@/utils/dateUtils';
import {
  formatBalticValue,
  formatBalticChange,
  getChangeColorClass,
  getBalticRouteDate,
  hasTIAssessment,
} from '@/utils/balticUtils';

const PERIOD_OPTIONS = [
  { value: 'daily' as const, label: 'Daily' },
  { value: 'weekly' as const, label: 'Weekly' },
  { value: 'monthly' as const, label: 'Monthly' },
  { value: 'yearly' as const, label: 'Yearly' },
];

function MiniSparkline({ points, delta }: { points: string; delta: number }) {
  return (
    <svg viewBox="0 0 100 36" className="h-7 w-[3.25rem] shrink-0 sm:h-8 sm:w-16" aria-hidden>
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={delta < 0 ? 'text-red-500' : delta > 0 ? 'text-emerald-500' : 'text-gray-400'}
      />
    </svg>
  );
}

export function BalticPreview() {
  const { routes, selectedPeriod, loading, fetchRoutes, setSelectedPeriod } = useBalticStore();
  const [routeHistories, setRouteHistories] = useState<Record<string, BalticHistoryPoint[]>>({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  useEffect(() => {
    const visibleRoutes = routes.slice(0, 4);
    if (visibleRoutes.length === 0) {
      setRouteHistories({});
      return;
    }

    let isCancelled = false;

    const loadHistory = async () => {
      try {
        const historyResults = await Promise.all(
          visibleRoutes.map(async (route) => {
            const history = await BalticService.getHistoryData({
              route: route.route,
              period: selectedPeriod,
            });

            const sortedHistory = [...history].sort(
              (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
            );

            return [route.route, sortedHistory] as const;
          })
        );

        if (!isCancelled) {
          setRouteHistories(Object.fromEntries(historyResults));
        }
      } catch {
        if (!isCancelled) {
          setRouteHistories({});
        }
      }
    };

    loadHistory();

    return () => {
      isCancelled = true;
    };
  }, [routes, selectedPeriod]);

  const handlePeriodChange = (period: BalticHistoryRequest['period']) => {
    setSelectedPeriod(period);
  };

  const headerAction = (
    <Link to="/baltic" className="inline-flex shrink-0">
      <Button
        variant="ghost"
        size="sm"
        rightIcon={<ArrowRight className="h-4 w-4" aria-hidden />}
        className="whitespace-nowrap !px-2 !py-2 sm:!px-3"
        title="View all Baltic data"
      >
        View all
      </Button>
    </Link>
  );

  if (loading) {
    return (
      <div className="space-y-2 sm:space-y-3">
        <DailyReportSectionHeader title="Baltic" action={headerAction} />
        <BalticPreviewSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-2 sm:space-y-3">
      <DailyReportSectionHeader title="Baltic" action={headerAction} />

      <Card noPadding className="overflow-hidden shadow-sm">
        <div className="border-b border-gray-200 bg-gray-100/70 px-2 py-1.5 dark:border-dark-700 dark:bg-dark-800/80 sm:px-3 sm:py-2">
          <div className="flex gap-0.5 sm:gap-1">
            {PERIOD_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handlePeriodChange(option.value)}
                className={`flex-1 rounded-md px-1 py-0.5 text-[11px] font-medium transition-colors sm:px-2 sm:py-0.5 sm:text-xs ${
                  selectedPeriod === option.value
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-600 shadow-sm hover:bg-gray-50 dark:bg-dark-700 dark:text-gray-300 dark:hover:bg-dark-600'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {routes.length > 0 ? (
          <div className="divide-y divide-gray-100 dark:divide-dark-700">
            {routes.slice(0, 4).map((route, index) => {
              const biChange = route.biValueChange || 0;
              const tiChange = route.tiValueChange || 0;
              // Some routes (e.g. TD34) have no TI assessment - the API still sends 0 for its values
              const hasTI = hasTIAssessment(route);
              const routeDate = getBalticRouteDate(route);
              const routeHistory = routeHistories[route.route] || [];
              const historyValues = routeHistory
                .map((point) => point.biValue)
                .filter((value): value is number => typeof value === 'number');
              const hasHistory = historyValues.length > 1;
              const minValue = hasHistory ? Math.min(...historyValues) : 0;
              const maxValue = hasHistory ? Math.max(...historyValues) : 0;
              const valueRange = maxValue - minValue || 1;
              const sparklinePoints = hasHistory
                ? historyValues
                    .map((value, pointIndex) => {
                      const x = (pointIndex / (historyValues.length - 1)) * 100;
                      const y = 30 - ((value - minValue) / valueRange) * 24;
                      return `${x},${y}`;
                    })
                    .join(' ')
                : '0,18 25,14 50,20 75,12 100,16';
              const sparklineDelta = hasHistory
                ? historyValues[historyValues.length - 1] - historyValues[0]
                : biChange;

              return (
                <div
                  key={route.route || index}
                  className="cursor-pointer px-2 py-1.5 transition-colors hover:bg-gray-50 dark:hover:bg-dark-700 sm:px-3 sm:py-2"
                  onClick={() =>
                    navigate(
                      `/baltic?route=${encodeURIComponent(route.route)}&period=${selectedPeriod}`
                    )
                  }
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-gray-900 dark:text-white sm:text-base">
                      {route.route}
                    </div>
                    <p className="line-clamp-1 text-[11px] leading-snug text-gray-500 dark:text-gray-400 sm:text-xs">
                      {route.description}
                    </p>
                  </div>

                  <div className="mt-1 grid grid-cols-[1fr_1fr_auto] items-end gap-x-2 gap-y-0 tabular-nums">
                    <div className="min-w-0">
                      <div className="text-[10px] font-medium uppercase leading-none text-gray-500 dark:text-gray-400 sm:text-[11px]">
                        BITR
                      </div>
                      <div className="mt-0.5 text-sm font-bold text-gray-900 dark:text-white sm:text-base">
                        {formatBalticValue(route.biValue, route.decimals)}
                      </div>
                      <div
                        className={`text-[11px] leading-snug sm:text-xs ${getChangeColorClass(biChange)}`}
                      >
                        {formatBalticChange(biChange, route.decimals)}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] font-medium uppercase leading-none text-gray-500 dark:text-gray-400 sm:text-[11px]">
                        TI
                      </div>
                      <div className="mt-0.5 text-sm font-bold text-gray-900 dark:text-white sm:text-base">
                        {hasTI ? formatBalticValue(route.tiValue, route.decimals) : 'N/A'}
                      </div>
                      {hasTI && (
                        <div
                          className={`text-[11px] leading-snug sm:text-xs ${getChangeColorClass(tiChange)}`}
                        >
                          {formatBalticChange(tiChange, route.decimals)}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col justify-end pb-px">
                      <MiniSparkline points={sparklinePoints} delta={sparklineDelta} />
                    </div>
                  </div>

                  <div className="mt-1 flex items-center justify-between border-t border-gray-100 pt-1 text-[10px] text-gray-500 dark:border-dark-700 dark:text-gray-400 sm:text-[11px]">
                    <span>Updated</span>
                    <span className="font-medium">
                      {routeDate ? formatBalticDate(routeDate) : '—'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            No Baltic routes available
          </div>
        )}
      </Card>
    </div>
  );
}
